//import packages
import React, { useState, useEffect, useCallback, useMemo } from "react";
import dayjs from "dayjs";
import debounce from "lodash/debounce";

import {
  Layout,
  Table,
  Row,
  Col,
  Button,
  Modal,
  message,
  Radio,
  Form,
  Input,
  Badge,
  Tooltip,
} from "antd";

import {
  DeleteFilled,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  KeyOutlined,
  FilterOutlined,
  DownOutlined,
  RightOutlined,
} from "@ant-design/icons";

import UserRolesAndRightsHOComponent from "../../../components/UserRolesAndRightsHOComponent/UserRolesAndRightsHOComponent";
import UserFilter from "./UserFilter";
import UserFormModal from "./UserFormModal";

import {
  getUsersList,
  deleteUsers,
} from "../../../api/UserTypeAPI";

import { passwordResetRequest } from "../../../api/AuthAPI";
import { getUserRoles } from "../../../api/UserRoleAPI";
import { lookUp } from "../../../api/LookUpAPI";

import authorization from "../../../auth/authorization";

import {
  StyledFilterButton,
} from "./Styles";
import styled from "styled-components";
import {
  StyledComponentWrapper,
  Title,
  TableHeader as SharedTableHeader,
  TitleControls,
  ActionButtonGroup,
  ExpandButton,
  ExpandedDetails,
  DetailItem,
  DetailLabel,
  DetailValue,
} from "../../SharedStyles";

const TableHeader = styled(SharedTableHeader)`
  align-items: flex-start;
`;

const TableWrapper = styled(StyledComponentWrapper)`
  max-width: 1480px;
  height: calc(100% - 48px);
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  margin: 24px auto;
  padding: 24px 28px;
  @media (max-width: 1800px) {
    max-width: 1300px;
  }
  @media (max-width: 1550px) {
    max-width: 1080px;
  }
  @media (max-width: 1300px) {
    max-width: 950px;
  }
  @media (max-width: 1199px) {
    max-width: 780px;
  }
  @media (max-width: 922px), (max-width: 788px) {
    max-width: 712px;
  }
  @media (max-width: 720px) {
    max-width: 372px;
  }
`;


const { confirm } = Modal;
const { Content } = Layout;

const Users = ({ userType: userRoleType }) => {
  const [userType, setuserType] = useState([]);
  const [active, setActive] = useState(true);
  const [editId, setEditId] = useState("");
  const [clients, setClients] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [allowedUserRoles, setAllowedUserRoles] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [isFormModalVisible, setIsFormModalVisible] = useState(false);
  const [form] = Form.useForm();

  const showClients = useCallback((userClients = []) => {
    Modal.info({
      title: "User Clients",
      content: (
        <ul>
          {userClients.map((id) => {
            const client = clients.find((client) => client._id === id);
            if (!client) return null;
            return (
              <li key={id}>
                <b>{client.name}</b>
              </li>
            );
          })}
        </ul>
      ),
      onOk() {},
    });
  }, [clients]);

  const handlePasswordReset = useCallback((userDetails) => {
    passwordResetRequest({
      email: userDetails.email,
    })
      .then((res) => {
        const { data } = res;
        if (data && data.success) {
          message.success(
            "Successfully identified the user account and initiated password reset! Reset link has been sent to the corresponding Email"
          );
        } else {
          const errorMessage = data?.message || "Failed to find account for password reset! Please check the user details";
          throw new Error(errorMessage);
        }
      })
      .catch((e) => message.error(e.message));
  }, []);

  const onPasswordResetConfirmation = useCallback((userDetails) => {
    confirm({
      title: `Do you want to intiate the password reset for ${userDetails.userType} User : ${userDetails.firstName}  ${userDetails.lastName}? The user will be getting the password reset email containing a link through which he/she can update the password`,
      icon: <ExclamationCircleOutlined />,
      onOk: () => handlePasswordReset(userDetails),
      onCancel() {},
    });
  }, [handlePasswordReset]);

  const baseColumns = useMemo(() => [{
    title: "Name",
    key: "name",
    width: 250,
    render: (userDetails) => (
      <>
        {userDetails.firstName} {userDetails.lastName}
      </>
    ),
  },
  {
    title: "User Type",
    dataIndex: "userType",
    key: "userType",
    width: 180,
  },
  { title: "Email", dataIndex: "email", key: "email", width: 320 },
  ], [userType]);

  const [columns, setColumns] = useState(baseColumns);

  const fetchUsers = useCallback(() => {
    getUsersList({ 
      status: active ? "active" : "deactive", 
      userRole, 
      searchValue 
    }).then(({ data }) => {
      const users = (data.payload || []).map((user) => ({
        ...user,
        key: user._id,
      }));
      setuserType(users);
    });
  }, [active, userRole, searchValue]);

  const fetchClients = () => {
    lookUp("client").then((res) => {
      if (res.data && res.data.payload) {
        const clientData = res.data.payload.map((clientItem) => ({
          ...clientItem,
          name: clientItem.name,
          key: clientItem._id,
        }));

        setClients(clientData);
      }
    });
  };

  const fetchUserRoles = () => {
    getUserRoles().then((res) => {
      if (res.data.success === true) {
        const allowedUserRoles = authorization.getAllowedUsers(userRoleType);
        const { data } = res;
        if (data && data.payload) {
          const { payload } = data;
          const allowedUserList = payload.filter((eachUserRole) =>
            allowedUserRoles.includes(eachUserRole.name)
          );
          setAllowedUserRoles(allowedUserList);
        }
      }
    });
  };

  const onDeletinguserType = (userId) => {
    deleteUsers(userId)
      .then((res) => {
        const { status } = res;
        if (status && status === 204) {
          message.success("Successfully Deleted the User");
          setEditId("");
        } else {
          const { data } = res;
          if (data && data.success) {
            message.success("Successfully Deleted the User");
            setEditId("");
          } else {
            if (data && data.message) {
              message.error(data.message);
            } else {
              message.error("Unable to delete the user. Please try again!");
            }
          }
        }
        fetchUsers();
      })
      .catch((error) => {
        console.log(
          "================== delete user error ========================",
          error
        );
        const { response } = error;
        if (response) {
          const { data } = response;
          if (data && data.message) {
            message.error(data.message);
          } else {
            message.error("Unable to delete the user. Please try again!");
          }
        } else {
          message.error("Unable to delete the user. Please try again!");
        }
      });
  };

  const showDeleteConfirmation = (userType, userId) => {
    confirm({
      title: `Do you want to delete User ${userType.firstName}  ${userType.lastName}?`,
      icon: <ExclamationCircleOutlined />,
      onOk: () => {
        onDeletinguserType(userId);
      },
      onCancel() { },
    });
  };

  const closeFormModal = useCallback(() => {
    setEditId("");
    setSelectedUser(null);
    setIsFormModalVisible(false);
  }, []);

  // Update columns when baseColumns or userRoleType changes
  useEffect(() => {
    const clonnedColumns = [...baseColumns];
    clonnedColumns.push({
      title: "Actions",
      dataIndex: "",
      key: "actions",
      width: 220,
      align: "center",
      render: (userDetails) => (
        <ActionButtonGroup>
          <Tooltip title="View clients">
            <Button
              shape="circle"
              icon={<EyeOutlined />}
              onClick={(event) => {
                event.stopPropagation();
                showClients(userDetails.clients);
              }}
            />
          </Tooltip>
          {['ops admin', 'developer'].includes(userDetails.userType) ? (
            <Tooltip title="Reset password">
              <Button
                shape="circle"
                icon={<KeyOutlined />}
                onClick={(event) => {
                  event.stopPropagation();
                  onPasswordResetConfirmation(userDetails);
                }}
              />
            </Tooltip>
          ) : null}
          {authorization.isAllowed(userRoleType, "adminUserUpdate") ? (
            <Tooltip title="Edit">
              <Button
                shape="circle"
                icon={<EditOutlined />}
                onClick={(event) => {
                  event.stopPropagation();
                  setEditId(userDetails._id);
                  setSelectedUser(userDetails);
                  setIsFormModalVisible(true);
                }}
              />
            </Tooltip>
          ) : null}
          {authorization.isAllowed(userRoleType, "adminUserDelete") ? (
            <Tooltip title="Delete">
              <Button
                shape="circle"
                icon={<DeleteFilled />}
                onClick={(event) => {
                  event.stopPropagation();
                  showDeleteConfirmation(userDetails, userDetails._id);
                }}
              />
            </Tooltip>
          ) : null}
        </ActionButtonGroup>
      ),
    });
    setColumns(clonnedColumns);
  }, [baseColumns, userRoleType, showClients, onPasswordResetConfirmation]);

  const onClearFilter = () => {
      setUserRole(null);
      setSearchValue("");
      setInputValue("");
      setFilterOpen(false)
  };

  // Debounced function to update searchValue
  const debouncedSetSearchValue = useMemo(
    () => debounce((value) => {
      setSearchValue(value);
    }, 500),
    []
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedSetSearchValue(value);
  };

  useEffect(() => {
    return () => {
      debouncedSetSearchValue.cancel();
    };
  }, [debouncedSetSearchValue]);

  useEffect(() => {
    fetchUsers();
  }, [active, userRole, searchValue]);

  useEffect(() => {
    fetchClients();
    fetchUserRoles();
  }, []);

  const getClientNames = (userClients = []) => {
    const clientNames = userClients
      .map((id) => clients.find((client) => client._id === id)?.name)
      .filter(Boolean);

    return clientNames.length ? clientNames.join(", ") : "--";
  };

  const renderExpandedRow = (userDetails) => (
    <ExpandedDetails>
      <DetailItem>
        <DetailLabel>Account Status</DetailLabel>
        <DetailValue>{userDetails.status || "--"}</DetailValue>
      </DetailItem>
      <DetailItem>
        <DetailLabel>Created Date</DetailLabel>
        <DetailValue>
          {userDetails.createDate ? dayjs(userDetails.createDate).format("LLLL") : "--"}
        </DetailValue>
      </DetailItem>
      <DetailItem>
        <DetailLabel>Last Login</DetailLabel>
        <DetailValue>
          {userDetails.lastLogin ? dayjs(userDetails.lastLogin).format("LLLL") : "--"}
        </DetailValue>
      </DetailItem>
      <DetailItem>
        <DetailLabel>Clients</DetailLabel>
        <DetailValue>{getClientNames(userDetails.clients)}</DetailValue>
      </DetailItem>
    </ExpandedDetails>
  );

  return (
    <Content style={{ height: '100%', overflow: 'hidden' }}>
      <TableWrapper>
        <UserFormModal
          open={isFormModalVisible}
          editId={editId}
          selectedUser={selectedUser}
          userRoles={allowedUserRoles}
          clients={clients}
          userRoleType={userRoleType}
          onClose={closeFormModal}
          onUserChange={fetchUsers}
          canDeactivateUser={authorization.isAllowed(userRoleType, "adminUserDelete")}
        />
        <TableHeader>
          <Title level={2}>All Admins
            <TitleControls>
              <Radio.Group value={active} size="small" onChange={(e) => setActive(e.target.value)}>
                <Radio.Button value={true}>Active</Radio.Button>
                <Radio.Button value={false}>Inactive</Radio.Button>
              </Radio.Group>
            </TitleControls>
          </Title>
          {authorization.isAllowed(userRoleType, "registerAdminUser") ? (
            <Button
              type="primary"
              size="large"
              onClick={() => {
                setEditId("");
                setSelectedUser(null);
                setIsFormModalVisible(true);
              }}
            >
              Add
            </Button>
          ) : null}
        </TableHeader>
        <Row>
          <Col span={24}>
            <Form name="search-form" layout="vertical" form={form}>
              <Form.Item>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <Input
                    allowClear
                    size="large"
                    placeholder="Search by Last Name or Email"
                    value={inputValue}
                    onChange={handleSearchChange}
                    onSearch={fetchUsers}
                    onClear={onClearFilter}
                  />
                  <Badge>
                    <StyledFilterButton
                      type="primary"
                      size={"large"}
                      icon={<FilterOutlined />}
                      onClick={() => setFilterOpen(true)}
                    />
                  </Badge>
                </div>
              </Form.Item>
            </Form>
          </Col>
        </Row>
        <Row style={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}>
          <Col span={24} style={{ height: '100%' }}>
            <Table
              columns={columns}
              dataSource={userType}
              expandable={{
                expandedRowRender: renderExpandedRow,
                expandRowByClick: true,
                expandIcon: ({ expanded, onExpand, record }) => (
                  <Tooltip title={expanded ? "Collapse details" : "Expand details"}>
                    <ExpandButton
                      shape="circle"
                      size="small"
                      type="text"
                      icon={expanded ? <DownOutlined /> : <RightOutlined />}
                      onClick={(event) => {
                        event.stopPropagation();
                        onExpand(record, event);
                      }}
                    />
                  </Tooltip>
                ),
              }}
              pagination={{
                pageSizeOptions: ["5", "10", "20", "50", "100"],
                showSizeChanger: true,
              }}
              scroll={{ x: 760, y: "calc(100vh - 380px)" }}
              tableLayout="fixed"
            />
          </Col>
        </Row>
      </TableWrapper>
      <UserFilter
        open={filterOpen}
        userRole={userRole}
        setUserRole={setUserRole}
        onFilter={fetchUsers}
        onClearFilter={onClearFilter}
        setFilterOpen={setFilterOpen}
      />
    </Content>
  );
};

export default UserRolesAndRightsHOComponent(Users);
